import React from 'react';
import { Box, CircularProgress, Typography, Fade } from '@mui/material';
import { keyframes } from '@mui/system';

// Custom pulse animation
const pulse = keyframes`
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.7;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

// Floating animation for dots
const float = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
`;

// Bars animation
const bars = keyframes`
  0%, 40%, 100% {
    transform: scaleY(0.4);
  }
  20% {
    transform: scaleY(1);
  }
`;

// Ripple animation
const ripple = keyframes`
  0% {
    transform: scale(0);
    opacity: 1;
  }
  100% {
    transform: scale(1);
    opacity: 0;
  }
`;

// Spinner rotation
const spin = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

// Bounce animation
const bounce = keyframes`
  0%, 80%, 100% {
    transform: scale(0);
  }
  40% {
    transform: scale(1);
  }
`;

// Wave animation
const wave = keyframes`
  0%, 60%, 100% {
    transform: initial;
  }
  30% {
    transform: translateY(-15px);
  }
`;

interface LoadingSpinnerProps {
  size?: 'small' | 'medium' | 'large';
  message?: string;
  variant?: 'circular' | 'dots' | 'pulse' | 'bars' | 'ripple' | 'spinner' | 'bounce' | 'wave';
  fullScreen?: boolean;
  color?: 'primary' | 'secondary' | 'inherit';
  speed?: 'slow' | 'normal' | 'fast';
}

const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = 'medium',
  message,
  variant = 'circular',
  fullScreen = false,
  color = 'primary',
  speed = 'normal',
}) => {
  const getSizeValue = () => {
    switch (size) {
      case 'small':
        return 24;
      case 'large':
        return 64;
      default:
        return 40;
    }
  };

  const getAnimationDuration = () => {
    switch (speed) {
      case 'slow':
        return '2s';
      case 'fast':
        return '0.8s';
      default:
        return '1.2s';
    }
  };

  const renderCircularLoader = () => (
    <CircularProgress
      size={getSizeValue()}
      color={color}
      sx={{
        animation: `${pulse} 2s ease-in-out infinite`,
      }}
    />
  );

  const renderDotsLoader = () => (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
      {[0, 1, 2].map((index) => (
        <Box
          key={index}
          sx={{
            width: size === 'small' ? 8 : size === 'large' ? 16 : 12,
            height: size === 'small' ? 8 : size === 'large' ? 16 : 12,
            borderRadius: '50%',
            backgroundColor: color === 'inherit' ? 'text.primary' : `${color}.main`,
            animation: `${float} 1.4s ease-in-out infinite`,
            animationDelay: `${index * 0.2}s`,
          }}
        />
      ))}
    </Box>
  );

  const renderPulseLoader = () => (
    <Box
      sx={{
        width: getSizeValue(),
        height: getSizeValue(),
        borderRadius: '50%',
        background: (theme) => {
          if (color === 'inherit') {
            return `linear-gradient(45deg, ${theme.palette.text.primary}, ${theme.palette.text.secondary})`;
          }
          return `linear-gradient(45deg, ${theme.palette[color].main}, ${theme.palette[color].light})`;
        },
        animation: `${pulse} ${getAnimationDuration()} ease-in-out infinite`,
      }}
    />
  );

  const renderBarsLoader = () => (
    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
      {[0, 1, 2, 3, 4].map((index) => (
        <Box
          key={index}
          sx={{
            width: size === 'small' ? 3 : size === 'large' ? 6 : 4,
            height: size === 'small' ? 16 : size === 'large' ? 32 : 24,
            backgroundColor: color === 'inherit' ? 'text.primary' : `${color}.main`,
            animation: `${bars} ${getAnimationDuration()} ease-in-out infinite`,
            animationDelay: `${index * 0.1}s`,
          }}
        />
      ))}
    </Box>
  );

  const renderRippleLoader = () => (
    <Box
      sx={{
        position: 'relative',
        width: getSizeValue(),
        height: getSizeValue(),
      }}
    >
      {[0, 1].map((index) => (
        <Box
          key={index}
          sx={{
            position: 'absolute',
            border: `2px solid ${color === 'inherit' ? 'currentColor' : `var(--${color}-color)`}`,
            opacity: 1,
            borderRadius: '50%',
            animation: `${ripple} ${getAnimationDuration()} cubic-bezier(0, 0.2, 0.8, 1) infinite`,
            animationDelay: `${index * 0.5}s`,
            width: '100%',
            height: '100%',
          }}
        />
      ))}
    </Box>
  );

  const renderSpinnerLoader = () => (
    <Box
      sx={{
        width: getSizeValue(),
        height: getSizeValue(),
        border: `3px solid ${color === 'inherit' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.1)'}`,
        borderTop: `3px solid ${color === 'inherit' ? 'currentColor' : `var(--${color}-color)`}`,
        borderRadius: '50%',
        animation: `${spin} ${getAnimationDuration()} linear infinite`,
      }}
    />
  );

  const renderBounceLoader = () => (
    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
      {[0, 1, 2].map((index) => (
        <Box
          key={index}
          sx={{
            width: size === 'small' ? 8 : size === 'large' ? 16 : 12,
            height: size === 'small' ? 8 : size === 'large' ? 16 : 12,
            borderRadius: '50%',
            backgroundColor: color === 'inherit' ? 'text.primary' : `${color}.main`,
            animation: `${bounce} ${getAnimationDuration()} ease-in-out infinite`,
            animationDelay: `${index * 0.16}s`,
          }}
        />
      ))}
    </Box>
  );

  const renderWaveLoader = () => (
    <Box sx={{ display: 'flex', gap: 0.25, alignItems: 'center' }}>
      {[0, 1, 2, 3, 4].map((index) => (
        <Box
          key={index}
          sx={{
            width: size === 'small' ? 2 : size === 'large' ? 4 : 3,
            height: size === 'small' ? 12 : size === 'large' ? 24 : 18,
            backgroundColor: color === 'inherit' ? 'text.primary' : `${color}.main`,
            animation: `${wave} ${getAnimationDuration()} ease-in-out infinite`,
            animationDelay: `${index * 0.1}s`,
          }}
        />
      ))}
    </Box>
  );

  const renderLoader = () => {
    switch (variant) {
      case 'dots':
        return renderDotsLoader();
      case 'pulse':
        return renderPulseLoader();
      case 'bars':
        return renderBarsLoader();
      case 'ripple':
        return renderRippleLoader();
      case 'spinner':
        return renderSpinnerLoader();
      case 'bounce':
        return renderBounceLoader();
      case 'wave':
        return renderWaveLoader();
      default:
        return renderCircularLoader();
    }
  };

  const content = (
    <Fade in timeout={300}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          ...(fullScreen && {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: (theme) =>
              theme.palette.mode === 'dark'
                ? 'rgba(15, 23, 42, 0.8)'
                : 'rgba(248, 250, 252, 0.8)',
            backdropFilter: 'blur(4px)',
            zIndex: 9999,
          }),
        }}
      >
        {renderLoader()}
        {message && (
          <Typography
            variant={size === 'small' ? 'caption' : 'body2'}
            color="text.secondary"
            sx={{
              fontWeight: 500,
              textAlign: 'center',
              maxWidth: 200,
              animation: `${pulse} 2s ease-in-out infinite`,
            }}
          >
            {message}
          </Typography>
        )}
      </Box>
    </Fade>
  );

  return content;
};

// Skeleton loader component for content placeholders
interface SkeletonLoaderProps {
  variant?: 'text' | 'rectangular' | 'circular';
  width?: number | string;
  height?: number | string;
  animation?: 'pulse' | 'wave';
  count?: number;
}

export const SkeletonLoader: React.FC<SkeletonLoaderProps> = ({
  variant = 'text',
  width = '100%',
  height = variant === 'text' ? 20 : 40,
  animation = 'pulse',
  count = 1,
}) => {
  const shimmer = keyframes`
    0% {
      background-position: -200px 0;
    }
    100% {
      background-position: calc(200px + 100%) 0;
    }
  };
  `;

  const getStyles = () => ({
    width,
    height,
    backgroundColor: (theme: any) =>
      theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
    borderRadius: variant === 'circular' ? '50%' : variant === 'text' ? 4 : 8,
    ...(animation === 'wave' && {
      background: (theme: any) =>
        `linear-gradient(90deg, ${
          theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
        } 25%, ${
          theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'
        } 50%, ${
          theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
        } 75%)`,
      backgroundSize: '200px 100%',
      animation: `${shimmer} 1.2s ease-in-out infinite`,
    }),
    ...(animation === 'pulse' && {
      animation: `${pulse} 1.5s ease-in-out infinite`,
    }),
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {Array.from({ length: count }).map((_, index) => (
        <Box key={index} sx={getStyles()} />
      ))}
    </Box>
  );
};

export default LoadingSpinner;